import { getAddress, keccak256, recoverMessageAddress, toBytes, toHex } from "viem";
import type { Address, Hex } from "viem";
import { H } from "./headers";

/** A signer of personal_sign messages: a viem account (the agent key, the submitter key, the wallet key). */
export interface MessageSigner {
  address: Address;
  signMessage(args: { message: string }): Promise<Hex>;
}

export function bodyHash(body: string | Uint8Array): Hex {
  return keccak256(typeof body === "string" ? toBytes(body) : body);
}

/** The message an agent signs for a paid call (CONTRACT.md 0.3): authentication only, no amount. */
export function requestMessage(service: string, call: string, hash: Hex): string {
  return `Woodpecker request\nservice: ${service}\ncall: ${call}\nbody: ${hash}`;
}

export async function signRequest(signer: MessageSigner, service: string, call: string, body: string | Uint8Array) {
  return signer.signMessage({ message: requestMessage(service, call, bodyHash(body)) });
}

export interface PaidHeaders {
  client: Address;
  identity: Address;
  call: string;
  signature: Hex;
  callback: string | null;
  close: boolean;
}

export class RequestError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message?: string,
  ) {
    super(message ?? code);
  }
}

/** Reads the four headers and verifies that the signature recovers to the identity. */
export async function verifyRequest(
  headers: Headers,
  body: string | Uint8Array,
  service: string,
): Promise<PaidHeaders> {
  const client = headers.get(H.client);
  const identity = headers.get(H.identity);
  const call = headers.get(H.call);
  const signature = headers.get(H.signature);
  if (!client || !identity || !call || !signature) throw new RequestError(401, "missing_headers");
  let clientAddr: Address;
  let identityAddr: Address;
  try {
    clientAddr = getAddress(client);
    identityAddr = getAddress(identity);
  } catch {
    throw new RequestError(401, "bad_signature", "malformed address");
  }
  let recovered: Address;
  try {
    recovered = await recoverMessageAddress({
      message: requestMessage(service, call, bodyHash(body)),
      signature: signature as Hex,
    });
  } catch {
    throw new RequestError(401, "bad_signature");
  }
  if (recovered.toLowerCase() !== identityAddr.toLowerCase()) throw new RequestError(401, "bad_signature");
  return {
    client: clientAddr,
    identity: identityAddr,
    call,
    signature: signature as Hex,
    callback: headers.get(H.callback),
    close: headers.get(H.close) === "1",
  };
}

/** The headers of a paid call, built by the DO. */
export async function paidHeaders(
  agent: MessageSigner,
  client: Address,
  service: string,
  call: string,
  body: string,
  opts: { callback?: string; close?: boolean } = {},
): Promise<Record<string, string>> {
  const out: Record<string, string> = {
    "content-type": "application/json",
    [H.client]: client,
    [H.identity]: agent.address,
    [H.call]: call,
    [H.signature]: await signRequest(agent, service, call, body),
  };
  if (opts.callback) out[H.callback] = opts.callback;
  if (opts.close) out[H.close] = "1";
  return out;
}

/** The sign-in challenge message (CONTRACT.md 0.2, D47). */
export interface Challenge {
  service: string;
  domain: string;
  wallet: string; // chainId:address
  nonce: Hex;
  expires: string; // ISO 8601
}

export function signInMessage(c: Challenge): string {
  return `Woodpecker sign-in\nservice: ${c.service}\ndomain: ${c.domain}\nwallet: ${c.wallet}\nnonce: ${c.nonce}\nexpires: ${c.expires}`;
}

/** The challenge as a service answers it: its fields and the text it wants signed. */
export interface ChallengeAnswer extends Challenge {
  message: string;
}

/**
 * The message a client signs for a sign-in: rebuilt from the challenge's fields, never the text the service sent, so
 * a service gets the wallet's signature over its own challenge only and never over another text (an identity root,
 * another service's challenge). `expected` names the service the client meant to sign in to, the wallet name and the
 * host it reached; a challenge that names anything else, or whose `message` is not its own fields, is refused.
 */
export function challengeToSign(
  c: ChallengeAnswer,
  expected: { service: string; wallet: string; domain: string },
): string {
  if (c.service !== expected.service)
    throw new Error(`the challenge names the service ${c.service}, not ${expected.service}`);
  if ((c.wallet ?? "").toLowerCase() !== expected.wallet.toLowerCase())
    throw new Error(`the challenge names the wallet ${c.wallet}, not ${expected.wallet}`);
  if (c.domain !== expected.domain)
    throw new Error(`the challenge names the domain ${c.domain}, not ${expected.domain}`);
  const message = signInMessage({
    service: c.service,
    domain: c.domain,
    wallet: c.wallet,
    nonce: c.nonce,
    expires: c.expires,
  });
  if (c.message !== message) throw new Error("the challenge's message is not its own fields: refused");
  return message;
}

/** A wallet-signed request to the host service's secret store (CONTRACT.md 8). */
export function secretMessage(service: string, deployment: string, time: string, hash: Hex): string {
  return `Woodpecker secret\nservice: ${service}\ndeployment: ${deployment}\ntime: ${time}\nbody: ${hash}`;
}

export function randomHex(bytes: number): Hex {
  const b = new Uint8Array(bytes);
  crypto.getRandomValues(b);
  return toHex(b);
}

/** A call id the DO assigns: 0x + 16 random bytes. */
export function newCallId(): string {
  return randomHex(16);
}
